import React from 'react'

/**
 * YetiMascot — Interactive Pure SVG Yeti Mascot Component
 * 
 * @param {boolean} isPasswordFocused - When true, Yeti covers its eyes with its arms.
 * @param {number} emailLength - Controls eye pupil position when typing email.
 * @param {boolean} isError - Triggers embarrassed/surprised expression on auth error.
 */
export default function YetiMascot({ isPasswordFocused = false, emailLength = 0, isError = false }) {
  // Calculate eye pupil offset based on input length
  const pupilX = Math.min(5, Math.max(-5, (emailLength - 8) * 0.4))
  const pupilY = Math.min(3, Math.max(-2, emailLength > 0 ? 2 : 0))

  return (
    <div style={{
      width: 140,
      height: 120,
      margin: '0 auto -10px',
      position: 'relative',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      userSelect: 'none',
      zIndex: 2
    }}>
      <svg
        width="150"
        height="130"
        viewBox="0 0 160 140"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: 'visible' }}
      >
        {/* Shadow */}
        <ellipse cx="80" cy="132" rx="55" ry="7" fill="rgba(30, 41, 59, 0.12)" />

        {/* Horns / Fur Ears */}
        <path
          d="M 38 48 C 22 28, 26 12, 42 22 C 48 26, 46 38, 44 45 Z"
          fill="var(--brand-blue-subtle, #EEF2FF)"
          stroke="var(--border-ink, #1E293B)"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <path
          d="M 122 48 C 138 28, 134 12, 118 22 C 112 26, 114 38, 116 45 Z"
          fill="var(--brand-blue-subtle, #EEF2FF)"
          stroke="var(--border-ink, #1E293B)"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />

        {/* Main Yeti Body */}
        <path
          d="M 30 135 C 24 100, 26 50, 48 32 C 60 22, 100 22, 112 32 C 134 50, 136 100, 130 135 Z"
          fill="var(--canvas-surface, #FFFFFF)"
          stroke="var(--border-ink, #1E293B)"
          strokeWidth="3"
          strokeLinejoin="round"
        />

        {/* Fur Tuft on Head */}
        <path
          d="M 68 24 Q 80 12 92 24 Q 84 18 80 23 Q 76 18 68 24 Z"
          fill="var(--canvas-surface, #FFFFFF)"
          stroke="var(--border-ink, #1E293B)"
          strokeWidth="2"
          strokeLinejoin="round"
        />

        {/* Face Patch (Soft Blue Accent) */}
        <ellipse
          cx="80"
          cy="74"
          rx="36"
          ry="30"
          fill="var(--brand-blue-subtle, #EEF2FF)"
          stroke="var(--border-ink, #1E293B)"
          strokeWidth="2"
        />

        {/* Cheek Blush */}
        <circle cx="56" cy="80" r="6" fill="rgba(226, 91, 60, 0.25)" />
        <circle cx="104" cy="80" r="6" fill="rgba(226, 91, 60, 0.25)" />

        {/* EYES */}
        {/* Left Eye Socket */}
        <circle
          cx="64"
          cy="68"
          r="10"
          fill="#FFFFFF"
          stroke="var(--border-ink, #1E293B)"
          strokeWidth="2"
        />
        {/* Right Eye Socket */}
        <circle
          cx="96"
          cy="68"
          r="10"
          fill="#FFFFFF"
          stroke="var(--border-ink, #1E293B)"
          strokeWidth="2"
        />

        {/* Eye Pupils with Eye Tracking */}
        {isError ? (
          <>
            {/* Error X eyes */}
            <path d="M 60 64 L 68 72 M 68 64 L 60 72" stroke="var(--brand-coral, #E25B3C)" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M 92 64 L 100 72 M 100 64 L 92 72" stroke="var(--brand-coral, #E25B3C)" strokeWidth="2.5" strokeLinecap="round" />
          </>
        ) : (
          <>
            {/* Left Pupil */}
            <circle
              cx={64 + pupilX}
              cy={68 + pupilY}
              r="5"
              fill="var(--ink-900, #121826)"
            />
            <circle
              cx={62 + pupilX}
              cy={66 + pupilY}
              r="1.8"
              fill="#FFFFFF"
            />

            {/* Right Pupil */}
            <circle
              cx={96 + pupilX}
              cy={68 + pupilY}
              r="5"
              fill="var(--ink-900, #121826)"
            />
            <circle
              cx={94 + pupilX}
              cy={66 + pupilY}
              r="1.8"
              fill="#FFFFFF"
            />
          </>
        )}

        {/* Snout & Nose */}
        <path
          d="M 74 78 Q 80 75 86 78 Q 83 83 77 83 Z"
          fill="var(--brand-blue, #2D5BFF)"
          stroke="var(--border-ink, #1E293B)"
          strokeWidth="1.5"
        />

        {/* Mouth */}
        {isError ? (
          <path d="M 72 90 Q 80 84 88 90" fill="none" stroke="var(--ink-900, #121826)" strokeWidth="2" strokeLinecap="round" />
        ) : isPasswordFocused ? (
          <circle cx="80" cy="88" r="3.5" fill="var(--ink-900, #121826)" />
        ) : (
          <path d="M 73 87 Q 80 93 87 87" fill="none" stroke="var(--ink-900, #121826)" strokeWidth="2" strokeLinecap="round" />
        )}

        {/* ── ARMS & PAWS (ANIMATED UPWARD TO COVER EYES) ── */}
        {/* Left Arm */}
        <g style={{
          transform: isPasswordFocused ? 'translate(28px, -24px) rotate(42deg)' : 'translate(0px, 0px) rotate(0deg)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transformOrigin: '25px 115px'
        }}>
          <path
            d="M 22 110 C 10 95, 12 75, 32 62 C 45 74, 42 96, 34 115 Z"
            fill="var(--canvas-surface, #FFFFFF)"
            stroke="var(--border-ink, #1E293B)"
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
          {/* Paw Pads */}
          <circle cx="30" cy="70" r="4" fill="var(--brand-blue-subtle, #EEF2FF)" />
          <circle cx="24" cy="76" r="2" fill="var(--brand-blue-subtle, #EEF2FF)" />
          <circle cx="34" cy="76" r="2" fill="var(--brand-blue-subtle, #EEF2FF)" />
        </g>

        {/* Right Arm */}
        <g style={{
          transform: isPasswordFocused ? 'translate(-28px, -24px) rotate(-42deg)' : 'translate(0px, 0px) rotate(0deg)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transformOrigin: '135px 115px'
        }}>
          <path
            d="M 138 110 C 150 95, 148 75, 128 62 C 115 74, 118 96, 126 115 Z"
            fill="var(--canvas-surface, #FFFFFF)"
            stroke="var(--border-ink, #1E293B)"
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
          {/* Paw Pads */}
          <circle cx="130" cy="70" r="4" fill="var(--brand-blue-subtle, #EEF2FF)" />
          <circle cx="136" cy="76" r="2" fill="var(--brand-blue-subtle, #EEF2FF)" />
          <circle cx="126" cy="76" r="2" fill="var(--brand-blue-subtle, #EEF2FF)" />
        </g>
      </svg>
    </div>
  )
}
